'use client';

import type { KeyboardEvent, ReactNode } from 'react';
import { Box, VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { MetaDefault } from '../../../../../components/typography';
import type { CardRenderContext } from '../../tool-config-types';
import { TraceLine } from '../../trace';
import { useResolvedSignalDisplayTitle } from '../../../hooks/useResolvedSignalDisplayTitle';
import {
  readPortalObjectResult,
  readPortalObjectTitle,
} from '../../../utils/portal-object-result';
import { FlatToolCard } from './flat-tool-card';
import { getHITLArtifactHeaderActions } from './hitl-artifact-header-actions';
import { PortalObjectReferenceCard } from './PortalObjectReferenceCard';
import { HitlActionOutcomeRow } from '../../HITL/components/HitlActionOutcomeRow';

type PortalObjectContentType = 'finding' | 'signal';

function readSignalId(input: unknown): string | undefined {
  if (!input || typeof input !== 'object' || Array.isArray(input)) {
    return undefined;
  }

  const signalId = (input as Record<string, unknown>).signalId;

  return typeof signalId === 'string' && signalId.trim() ? signalId : undefined;
}

function readNonEmptyString(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined;
}

function readToolFailureMessage(
  toolCallData: CardRenderContext['toolCallData'],
): string | undefined {
  if (toolCallData.status !== 'error') {
    return undefined;
  }

  const output =
    toolCallData.output &&
    typeof toolCallData.output === 'object' &&
    !Array.isArray(toolCallData.output)
      ? (toolCallData.output as Record<string, unknown>)
      : undefined;

  return (
    readNonEmptyString(toolCallData.error) ??
    readNonEmptyString(output?.message) ??
    readNonEmptyString(output?.errorCode) ??
    'Unknown error'
  );
}

function PortalObjectHitlCard({
  ctx,
  contentType,
}: {
  ctx: CardRenderContext;
  contentType: PortalObjectContentType;
}): ReactNode {
  const t = useTranslations(
    'designSystem.chat.hitlRequestConfirmationPromptCard',
  );
  const tObjectReference = useTranslations('designSystem.chat.objectReference');
  const inputTitle = readPortalObjectTitle(ctx.toolCallData.input);
  const isUpdate =
    ctx.toolName.toLowerCase().includes('update') ||
    ctx.toolName.toLowerCase().includes('revise');
  const isSignalUpdate = contentType === 'signal' && isUpdate;
  const resolvedSignalTitle = useResolvedSignalDisplayTitle({
    signalId:
      contentType === 'signal'
        ? readSignalId(ctx.toolCallData.input)
        : undefined,
    title: inputTitle,
    enabled: contentType === 'signal' && !inputTitle,
  });
  const fallbackTitle =
    contentType === 'signal'
      ? isSignalUpdate
        ? t('signalUpdateReviewFallbackTitle')
        : t('signalReviewFallbackTitle')
      : t('findingReviewFallbackTitle');
  const title = inputTitle ?? resolvedSignalTitle ?? fallbackTitle;
  const createdObject = readPortalObjectResult({
    input: ctx.toolCallData.input,
    output: ctx.toolCallData.output,
    contentType,
    isCompleted:
      ctx.toolCallData.status === 'completed' ||
      ctx.toolCallData.output !== undefined,
  });
  const failureMessage = readToolFailureMessage(ctx.toolCallData);
  const canOpenReview = Boolean(
    !createdObject &&
      !failureMessage &&
      ctx.onOpenReview &&
      !ctx.hitlDecisionType,
  );
  const isCreating =
    (ctx.hitlDecisionType === 'approve' || ctx.hitlDecisionType === 'edit') &&
    !createdObject &&
    !failureMessage;
  const creatingMessage =
    contentType === 'finding'
      ? t('findingCreatingStatus')
      : t('signalCreatingStatus');
  const handleOpenReview = () => {
    if (!canOpenReview) {
      return;
    }

    ctx.onOpenReview?.();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    handleOpenReview();
  };

  const objectReferencePrefix =
    contentType === 'signal'
      ? isUpdate
        ? tObjectReference('updatedSignal')
        : tObjectReference('createdSignal')
      : isUpdate
        ? tObjectReference('updatedFinding')
        : tObjectReference('createdFinding');

  return (
    <VStack alignItems="stretch" gap={1.5} width="100%">
      <FlatToolCard
        title={ctx.toolName}
        isRunning={isCreating}
        headerActions={getHITLArtifactHeaderActions(ctx)}
      >
        {createdObject ? undefined : (
          <Box
            role={canOpenReview ? 'button' : undefined}
            tabIndex={canOpenReview ? 0 : undefined}
            aria-label={canOpenReview ? title : undefined}
            onClick={canOpenReview ? handleOpenReview : undefined}
            onKeyDown={canOpenReview ? handleKeyDown : undefined}
            width="full"
            textAlign="left"
            cursor={canOpenReview ? 'pointer' : 'default'}
          >
            <MetaDefault color="fg.muted">{title}</MetaDefault>
          </Box>
        )}
      </FlatToolCard>
      {isCreating ? (
        <TraceLine title={creatingMessage} status="running" />
      ) : null}
      {failureMessage ? (
        <HitlActionOutcomeRow failureMessage={failureMessage} />
      ) : null}
      {createdObject ? (
        <PortalObjectReferenceCard
          objectType={createdObject.objectType}
          objectId={createdObject.objectId}
          title={createdObject.title ?? title}
          prefix={objectReferencePrefix}
          onOpenObject={ctx.onOpenObject}
        />
      ) : null}
    </VStack>
  );
}

export function renderFindingHitlCard(ctx: CardRenderContext): ReactNode {
  return <PortalObjectHitlCard ctx={ctx} contentType="finding" />;
}

export function renderSignalHitlCard(ctx: CardRenderContext): ReactNode {
  return <PortalObjectHitlCard ctx={ctx} contentType="signal" />;
}
